import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    Component,
    ElementRef,
    Host,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    inject
} from '@angular/core';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { App } from '@df/core/app';
import { FacetOverridePipe } from '@df/module-facet-url/util/facet-override.pipe';
import { AfterContentInitDirective, AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { PAGE_HEADER_VERSION_CONFIG } from '../../../layout/page/header/page-header.config';
import { NPageHeader } from '../../../layout/page/header/page-header.interface';
import { PageHeaderService } from '../../../layout/page/header/page-header.service';
import { ReadMoreComponent } from '../../../ui/common/read-more.component';
import { ShellComponent } from '../../../ui/display/local-shell.component';
import { ProductListingComponent } from '../../../ui/listing/product-listing.component';
import { ICmsOutletBlockComponent } from '../../block/icms-outlet-block.component';
import { BreadcrumbsComponent } from '../../meta/breadcrumbs.component';
import { CategoryViewComponent } from './category-view.component';
/**
 * Block data interface
 */
export interface ICategoryViewLayoutData extends Catalog.IBlockData<ICategoryViewLayoutContent> { }
/**
 * Block content interface
 */
export interface ICategoryViewLayoutContent {
    /**
     * Top components list
     */
    topComponents: NICmsContentComponent.IData[];
    /**
     * Middle components list
     */
    middleComponents: NICmsContentComponent.IData[];
    /**
     * If name ros should be hidden
     */
    nameRowVisible: boolean;
    /**
     * Bottom components list
     */
    bottomComponents: NICmsContentComponent.IData[];

    additional: NPageHeader.IVersionConfig;
}
/**
 * Component definition
 */
@Component({
    selector: 'category-view-layout',
    templateUrl: './category-view-layout.html',
    standalone: true,
    imports: [
        CommonModule,
        ICmsOutletBlockComponent,
        ICmsClientModule,
        ProductListingComponent,
        FacetOverridePipe,
        ReadMoreComponent,
        BreadcrumbsComponent,
        ShellComponent,
        IfSizeDirective,
        SizeClassDirective,
        AfterContentInitDirective
    ],
    providers: [PageHeaderService]
})
@ICmsComponent<CategoryViewLayoutComponent>({
    id: 'category-page-layout',
    block: {
        url: 'category-page-layout'
    },
    onDemandOnly: true,
    name: 'Category page layout',
    description: 'Category page layout block',
    fields: [
        {
            type: NICmsContentField.EFieldType.label,
            key: 'label-header',
            title: 'Header part'
        },
        <NICmsContentField.IComponentsField>{
            key: 'topComponents',
            type: NICmsContentField.EFieldType.components,
            title: 'Top components, above name',
            itemName: 'component',
            default: []
        },
        {
            type: NICmsContentField.EFieldType.toggle,
            key: 'nameRowVisible',
            title: 'Name row visible',
            default: true
        },
        <NICmsContentField.IComponentsField>{
            key: 'middleComponents',
            type: NICmsContentField.EFieldType.components,
            title: 'Middle components, below name',
            itemName: 'component',
            default: []
        },
        {
            type: NICmsContentField.EFieldType.label,
            key: 'label-bottom',
            title: 'Bottom part'
        },
        <NICmsContentField.IComponentsField>{
            key: 'bottomComponents',
            type: NICmsContentField.EFieldType.components,
            title: 'Bottom components',
            itemName: 'component',
            default: []
        },
        ...PAGE_HEADER_VERSION_CONFIG
    ]
})
export class CategoryViewLayoutComponent
    extends UiComponent
    implements NICmsContentComponent.IComponent, OnInit, OnChanges, OnDestroy, AfterViewInit {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: ICategoryViewLayoutContent;

    @Input()
    variant?: string;

    protected override canDetach = false;
    /**
     * Category shorthand
     */
    get category() {
        return this.categoryView.model;
    }
    /**
     * Dependencies
     */
    constructor(
        protected elementRef: ElementRef,
        protected renderer2: Renderer2,
        protected app: App,
        private afterContentInit: AfterContentInitDirectiveService,
        @Host()
        private pageHeaderService: PageHeaderService
    ) {
        super();
    }
    categoryView = inject(CategoryViewComponent);

    override ngOnInit() {
        this.afterContentInit.lock(this);
        super.ngOnInit();
    }
    ngAfterViewInit(): void {
        this.timeoutService.setTimeout(() => {
            this.afterContentInit.unlock(this);
        }, 1000);
    }
    override ngOnDestroy(): void {
        this.afterContentInit.unlock(this);
        super.ngOnDestroy();
        // reset page header settings
        this.timeoutService.platformSafe(() => this.pageHeaderService.resetHighPriorityState()); // injector may be destroyed
    }

    private updateClassNames(): void {
        this.pageHeaderService.setHighPriorityState({
            transparent: this.transparent,
            lightMode: this.lightMode,
            hideUp: this.hideUp
        });
    }
    private get transparent(): boolean {
        return !!this.content?.additional?.headerTransparent;
    }
    private get lightMode(): boolean {
        return !!this.content?.additional?.headerLightMode;
    }
    private get hideUp(): boolean {
        return !!this.content?.additional?.headerHideUp;
    }
    /////////////////// HELPERS ////////////////////
    override detectChanges() {
        this.updateClassNames();
        this.client.isCms ? this.categoryView.detectChanges() : super.detectChanges();
    }
}
